// @flow

/**
 * Composed Graph Display Component
 * Renders a flexible chart supporting mixed line and bar series.
 * Each series specifies its own render type via seriesType: 'line' | 'bar'.
 * Supports dual Y axes when series have different valueTypes.
 */

import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useCurrentPng } from 'recharts-to-png';
import DisplayWrapper from '../shared/DisplayWrapper';
import DisplayTitle from '../shared/DisplayTitle';
import ExportCsvButton from '../shared/ExportCsvButton';
import DownloadImageButton from '../shared/DownloadImageButton';
import { getSeriesColour } from '../shared/colours';
import { formatValue } from '../shared/formatters';
import { buildAxisAssignments } from '../shared/axisUtils';
import ChartTooltip from '../shared/ChartTooltip';
import { useAnswerToggle } from '../../answer-blocks';

type YAxisDef = {
  key: string,
  label: string,
  valueType: string,
  seriesType: 'line' | 'bar',
};

type Props = {
  definition: {
    displayType: 'composedGraph',
    title: string,
    xAxisKey: string,
    xAxisLabel?: string,
    xAxisType: string,
    yAxes: YAxisDef[],
    stacked?: boolean,
    data: Object[],
  },
  displayId?: string,
};

const ComposedGraphDisplay = ({ definition, displayId }: Props) => {
  const { title, xAxisKey, xAxisLabel, xAxisType, yAxes, stacked = false, data } = definition;
  const [getPng, { ref, isLoading }] = useCurrentPng();

  // Disable series entry animation during export: a freshly mounted (clipped)
  // chart would otherwise be captured mid-animation, i.e. half-drawn. Tolerates
  // a null context (chart rendered outside a chat message).
  const answerToggle = useAnswerToggle();
  const exportMode = answerToggle ? answerToggle.exportMode : false;

  const axisGroups = useMemo(() => buildAxisAssignments(yAxes), [yAxes]);
  const hasDualAxes = axisGroups.length > 1;

  const seriesAxisMap = useMemo(() => {
    const map = {};
    for (const group of axisGroups) {
      for (const axis of group.axes) {
        map[axis.key] = group.yAxisId;
      }
    }
    return map;
  }, [axisGroups]);

  const csvColumns = useMemo(() => [
    { key: xAxisKey, header: xAxisLabel || xAxisKey },
    ...yAxes.map(y => ({ key: y.key, header: y.label })),
  ], [xAxisKey, xAxisLabel, yAxes]);

  return (
    <DisplayWrapper $displayId={displayId}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <DisplayTitle style={{ marginBottom: 0 }}>{title}</DisplayTitle>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <DownloadImageButton title={title} getPng={getPng} isLoading={isLoading} />
          <ExportCsvButton title={title} columns={csvColumns} rows={data} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart ref={ref} data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => formatValue(v, xAxisType)}
          />
          {axisGroups.map((group) => (
            <YAxis
              key={group.yAxisId}
              yAxisId={group.yAxisId}
              orientation={group.orientation}
              tick={{ fontSize: 11 }}
              tickFormatter={(v) => formatValue(v, group.valueType)}
              label={{
                value: group.label,
                angle: group.orientation === 'left' ? -90 : 90,
                position: 'insideMiddle',
                fontSize: 11,
                fill: 'rgba(0,0,0,0.5)',
                dx: group.orientation === 'left' ? -15 : 15,
              }}
            />
          ))}
          <Tooltip content={<ChartTooltip yAxes={yAxes} />} />
          {yAxes.length > 1 && <Legend wrapperStyle={{ fontSize: '0.8rem' }} />}
          {/* Render bars first so lines appear on top */}
          {yAxes.map((axis, i) =>
            axis.seriesType === 'bar' && (
              <Bar
                key={axis.key}
                dataKey={axis.key}
                name={axis.label}
                yAxisId={seriesAxisMap[axis.key]}
                fill={getSeriesColour(i)}
                stackId={stacked ? (seriesAxisMap[axis.key] || 'left') : undefined}
                radius={stacked ? undefined : [3, 3, 0, 0]}
                isAnimationActive={!exportMode}
              />
            )
          )}
          {/* Render lines on top */}
          {yAxes.map((axis, i) =>
            axis.seriesType === 'line' && (
              <Line
                key={axis.key}
                type="monotone"
                dataKey={axis.key}
                name={axis.label}
                yAxisId={seriesAxisMap[axis.key]}
                stroke={getSeriesColour(i)}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                isAnimationActive={!exportMode}
              />
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </DisplayWrapper>
  );
};

export default ComposedGraphDisplay;
